import React, { useCallback } from 'react';
import cn from 'classnames';

import ICO_PATCH_TEST from 'assets/images/icons/eye-dropper.svg';
import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import Foldout from 'components/generic/Foldout.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import useAfn from 'hooks/useAfn.hook';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './PatchTestPanel.module.scss';

const L = logger('PatchTestPanel');

const PatchTestPanel = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { value, isLoading, error, update } = useNcobSettings();
  const { salonNcobPatchTestCopy } = value ?? {};

  const [text, setText] = useResettingState(salonNcobPatchTestCopy, useResettingState.NIL);
  const { val: isDirty, inv: isPristine, yay: markDirty, nay: markPristine } = useTip(false);

  const isEmpty = fromAnyToString(text).length === 0;
  const isHidden = isPristine || isEmpty;
  const isDisabled = isPristine || isEmpty;

  const [, handleSave] = useAfn(
    async ({ raw }) => {
      if (raw === null) return;
      const { ok, value } = await update({ data: { salonNcobPatchTestCopy: text } });
      if (!ok) return;
      setText(fromAnyToString(asObject(value).salonNcobPatchTestCopy));
      markPristine();
    },
    [markPristine, setText, text, update],
  );

  const handleTextChange = useCallback(
    (ev) => {
      setText(fromAnyToString(ev?.raw));
      markDirty();
    },
    [setText, markDirty],
  );

  if (error) L.debug('()', { isLoading, isDirty, text, error, value });

  return (
    <div
      data-bem="PatchTestPanel"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}

      <Foldout tag="PatchTestPanel" src={ICO_PATCH_TEST} title={t('onlineBooking.policyAndTerms.patchTest.title')}>
        <div data-bem="PatchTestPanel__container" className={CN.container}>
          <div className={CN.message}>
            <div className={CN.content}>
              <div className={CN.sectionTitle}>{t('onlineBooking.policyAndTerms.patchTest.title')}</div>
              <div className={CN.text}></div>
            </div>
          </div>

          <div data-bem="PatchTestPanel__note">
            <Note autoscroll hasExternalValue name="note" onChange={handleTextChange} tag="PatchTestPanel__note" value={text} variant="expanded" />
            <div
              className={cn({
                [CN.buttonContainer]: true,
                [CN.hidden]: isHidden,
              })}
            >
              <Button variant="primary" onClick={handleSave} disabled={isDisabled}>
                {isDirty ? t('common.buttons.saveUpp') : t('common.buttons.editUpp')}
              </Button>
            </div>
          </div>
        </div>
      </Foldout>
    </div>
  );
};

export default PatchTestPanel;
